import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/Logo";
import { useTheme } from "@/context/ThemeContext";
import {
  Printer, Cloud, Wallet, Shield, BarChart3, Zap, Users, FileText,
  CheckCircle2, ArrowRight, Sun, Moon,
} from "lucide-react";

const FEATURES = [
  { icon: Cloud, title: "Cloud PDF Upload", desc: "Upload from anywhere. Print anywhere." },
  { icon: Zap, title: "Silent Auto-Print", desc: "No popups. No clicks. Just prints." },
  { icon: Wallet, title: "Retailer Wallet", desc: "Auto deduct, recharge, transaction history." },
  { icon: Users, title: "Multi-role", desc: "Admin, Distributor, Retailer, Customer." },
  { icon: BarChart3, title: "Real-time Reports", desc: "Daily · Weekly · Monthly analytics." },
  { icon: Shield, title: "Enterprise Security", desc: "JWT auth, encrypted passwords, session control." },
];

const PRINTERS = ["Epson", "Canon", "HP", "Brother", "Thermal"];

export default function Landing() {
  const nav = useNavigate();
  const { theme, toggle } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="border-b border-border bg-background/85 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <Logo size={36} />
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="landing-theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <Button variant="ghost" onClick={() => nav("/login")} data-testid="landing-login-btn">
              Sign in
            </Button>
            <Button
              className="bg-primary hover:bg-brand-blue-hover text-primary-foreground"
              onClick={() => nav("/register")}
              data-testid="landing-signup-btn"
            >
              Get started <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 pt-20 pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Badge className="bg-brand-green/10 text-brand-green border border-brand-green/20 hover:bg-brand-green/10">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-green mr-2 animate-pulse" />
                Now live across Telangana & India
              </Badge>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05]">
                The <span className="text-brand-blue">smartest</span> way to run your{" "}
                <span className="text-brand-green">print centre</span>.
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
                MaaPrint is the cloud printing platform built for CSC centres, Meeseva agents,
                photo studios, schools and offices. Upload PDF · Auto-print · Auto-charge wallet ·
                Track every rupee.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-brand-blue-hover text-primary-foreground h-12 px-6"
                  onClick={() => nav("/register")}
                  data-testid="hero-cta-primary"
                >
                  Start printing free <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 px-6"
                  onClick={() => nav("/login")}
                  data-testid="hero-cta-secondary"
                >
                  I have an account
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                {["No installation", "Silent background print", "Wallet + Commissions"].map((t) => (
                  <span key={t} className="flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="text-brand-green" /> {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right visual */}
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl border border-border bg-card p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-red-500" />
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    <span className="h-2 w-2 rounded-full bg-brand-green" />
                  </div>
                  <span className="text-xs text-muted-foreground font-mono">maaprint.in/print</span>
                </div>
                <div className="mt-6 space-y-4">
                  <div className="p-4 rounded-lg border border-border bg-background">
                    <div className="flex items-center gap-3">
                      <FileText className="text-brand-blue" size={20} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">aadhaar_verify_form.pdf</p>
                        <p className="text-xs text-muted-foreground">4 pages · A4 · B&amp;W</p>
                      </div>
                      <Badge className="bg-brand-green text-white">Printed</Badge>
                    </div>
                    <div className="mt-3 h-1 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full w-full print-line" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg border border-border bg-background">
                      <p className="text-xs text-muted-foreground">Today’s prints</p>
                      <p className="text-2xl font-display font-semibold tabular-nums">247</p>
                    </div>
                    <div className="p-3 rounded-lg border border-border bg-background">
                      <p className="text-xs text-muted-foreground">Revenue</p>
                      <p className="text-2xl font-display font-semibold tabular-nums text-brand-green">₹1,948</p>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg border border-border bg-background flex items-center gap-3">
                    <Printer className="text-brand-blue" size={18} />
                    <span className="text-sm">Front Desk – Epson L3250</span>
                    <Badge className="ml-auto bg-brand-green text-white">Online</Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Printer brand strip */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Works with</span>
            {PRINTERS.map((b) => (
              <span key={b} className="font-display font-semibold text-muted-foreground">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest text-brand-blue">Platform</p>
            <h2 className="mt-2 text-3xl lg:text-4xl font-display font-semibold tracking-tight">
              Everything a modern print centre needs. Nothing it doesn’t.
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="p-6 rounded-xl border border-border bg-card hover:shadow-sm transition-all hover:-translate-y-[2px]"
                  data-testid={`feature-${i}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold">{f.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-gradient-to-br from-brand-blue to-brand-green">
        <div className="max-w-4xl mx-auto text-center px-4 lg:px-8 py-20">
          <h2 className="text-3xl lg:text-4xl font-display font-bold text-white tracking-tight">
            Print smarter. Earn more.
          </h2>
          <p className="mt-3 text-white/85">Join thousands of retailers powering their business with MaaPrint.</p>
          <div className="mt-8 flex justify-center gap-3">
            <Button
              size="lg"
              className="bg-white text-brand-blue hover:bg-white/90 h-12 px-6"
              onClick={() => nav("/register")}
              data-testid="cta-signup-btn"
            >
              Create free account
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent text-white border-white/50 hover:bg-white/10 hover:text-white h-12 px-6"
              onClick={() => nav("/login")}
              data-testid="cta-login-btn"
            >
              Sign in
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size={28} />
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} MaaPrint · Smart Cloud Printing Platform
          </p>
          <div className="flex gap-4 text-sm text-muted-foreground">
            <Link to="/login">Sign in</Link>
            <Link to="/register">Register</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
